// src/pages/dashboard/SelectProject.js
import { useEffect, useState, useContext } from "react";
import { Card, Button, Spin, message, Modal } from "antd";
import {
  ProjectOutlined, LogoutOutlined,
  CheckCircleFilled,
  ClockCircleOutlined,
  RocketOutlined
} from "@ant-design/icons";
import axios from "../../../utils/axios";
import { useNavigate } from "react-router-dom";
import { RootContext } from '../../../context/RootContext';
 
const SelectProject = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user"));
  const organizationId = localStorage.getItem('organizationId');
  const { dispatch } = useContext(RootContext);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const payload = {
        username: user?.username,
        organizationId
      };
      const res = await axios.post("/select-project-list", payload);
      if(res?.data?.projects){
        setProjects(res.data.projects);
      }
    } catch (err) {
      message.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };
 
  const handleContinue = () => {
    if (!selectedProject) return;

    const isHkbk = user.username === 'admin';

    // 🚫 Check Camera Config + Dashboard Setup
    if (!selectedProject.isCameraConfigured && !isHkbk) {
      Modal.warning({
        title: (
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <ClockCircleOutlined className="text-2xl text-purple-600" />
            </div>
            <span className="text-xl font-semibold text-gray-800">
              Dashboard Setup Unavailable
            </span>
          </div>
        ),
        content: (
          <div className="ml-15 space-y-4 pt-4">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-purple-400 p-4 rounded-r-lg">
              <p className="text-gray-700 font-medium mb-2">
                ⚠️ Camera configuration or dashboard setup is not completed for this project.
              </p>
            </div>
            <div className="space-y-2 text-gray-600">
              <p className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>Complete camera configuration for this project</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>Ensure dashboard setup is finished</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                <span>Contact your administrator if needed</span>
              </p>
            </div>
          </div>
        ),
        okText: "Understood",
        width: 520,
        okButtonProps: {
          className:
            "bg-gradient-to-r from-purple-600 to-blue-600 border-0 h-10 px-8 rounded-lg font-semibold hover:from-purple-700 hover:to-blue-700",
        },
        icon: null,
        centered: true,
      });
      return;
    }

    // ✅ Save selected project globally
    localStorage.setItem("selectedProject", selectedProject.projectname);

    dispatch({
      type: "SET_SELECTED_PROJECT",
      payload: selectedProject,
    });

    const specialProjects = ['Attendance Monitoring', 'Canteen Monitoring'];

    if (specialProjects.includes(selectedProject.projectname)) {
      navigate("/user", {
        state: { projectname: selectedProject.projectname }
      });
    } else {
      navigate("/userhome/Dashboard", {
        state: { projectname: selectedProject.projectname }
      });
    }
  };
 
  const logouts = () => {
    localStorage.clear();
    dispatch({type: 'LOGOUT'});
    navigate('/login');
  }
 
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 p-6 relative overflow-hidden">

      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
      
      {/* Logout Button */}
      <div className="absolute top-8 right-8 z-10">
        <Button
          danger
          type="text"
          size="large"
          icon={<LogoutOutlined />}
          className="flex items-center gap-2 rounded-xl px-6 h-12 font-medium
                     bg-white/80 backdrop-blur-sm shadow-lg hover:shadow-xl
                     border border-gray-200 hover:border-red-300 transition-all duration-300"
          onClick={logouts}
        >
          Logout
        </Button>
      </div>
 
      <div className="flex items-center justify-center min-h-screen">
        <Card
          className="w-full max-w-5xl rounded-3xl shadow-2xl border-0 backdrop-blur-sm bg-white/95"
          bodyStyle={{ padding: '3rem' }}
        >
          {/* Header Section */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-600 mb-4 shadow-lg">
              <ProjectOutlined className="text-4xl text-white" />
            </div>
            <h2 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-3">
              Select Your Project
            </h2>
            <p className="text-gray-500 text-lg">
              Choose a project to configure your dashboard experience
            </p>
          </div>
 
          {/* Loading State */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Spin size="large" />
              <p className="text-gray-500 mt-4">Loading your projects...</p>
            </div>
          ) : projects.length === 0 ? (
            /* Empty State */
            <div className="text-center py-20">
              <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gray-100 mb-6">
                <ProjectOutlined className="text-5xl text-gray-400" />
              </div>
              <p className="text-xl font-semibold text-gray-700 mb-2">
                No Projects Available
              </p>
              <p className="text-gray-500 max-w-md mx-auto">
                It looks like you don't have any projects yet. Contact your administrator or create a new project to get started.
              </p>
            </div>
          ) : (
            /* Project Cards Grid */
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {projects.map((project) => {
                  const active = selectedProject?._id === project._id;
                  return (
                    <div
                      key={project._id}
                      onClick={() => setSelectedProject(project)}
                      className={`group cursor-pointer rounded-2xl p-6 border-2 transition-all duration-300 transform
                        ${active
                          ? "border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50 shadow-xl scale-[1.02] ring-4 ring-blue-100"
                          : "border-gray-200 bg-white hover:border-blue-300 hover:shadow-lg hover:scale-[1.01]"
                        }
                      `}
                    >
                      {/* Project Header */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-start gap-3 flex-1">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300
                            ${active
                              ? 'bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg'
                              : 'bg-gray-100 group-hover:bg-blue-50'
                            }`}
                          >
                            <RocketOutlined className={`text-xl ${active ? 'text-white' : 'text-gray-600 group-hover:text-blue-600'}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-bold text-xl text-gray-800 mb-1">
                              {project.projectname}
                            </h3>
                            <p className="text-sm text-gray-600">
                              <span className="font-medium text-blue-600">{project.mainCategory}</span>
                            </p>
                          </div>
                        </div>
 
                        {/* Selection Indicator */}
                        {active && (
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600
                                        text-white flex items-center justify-center font-bold shadow-lg
                                        animate-bounce">
                            <CheckCircleFilled className="text-lg" />
                          </div>
                        )}
                      </div>
 
                      {/* Status Badges */}
                      <div className="flex flex-wrap gap-2 mt-4">
                        <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs font-medium border border-gray-200">
                          {project.version}
                        </div>
                        
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5
                            ${project.active
                              ? "bg-green-50 text-green-700 border border-green-200"
                              : "bg-red-50 text-red-700 border border-red-200"
                            }`}
                        >
                          <span className={`w-2 h-2 rounded-full ${project.active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                          {project.active ? "Active" : "Inactive"}
                        </span>
 
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1.5
                            ${project.isCameraConfigured
                              ? "bg-blue-50 text-blue-700 border border-blue-200"
                              : "bg-orange-50 text-orange-700 border border-orange-200"
                            }`}
                        >
                          {project.isCameraConfigured ? (
                            <CheckCircleFilled className="text-xs" />
                          ) : (
                            <ClockCircleOutlined className="text-xs" />
                          )}
                          {project.isCameraConfigured ? "Ready" : "Setup Pending"}
                        </span>
                      </div>
 
                      {/* Hover Effect Border */}
                      <div className={`absolute inset-0 rounded-2xl pointer-events-none transition-opacity duration-300
                        ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                        bg-gradient-to-r from-blue-500/10 to-purple-500/10`}
                      ></div>
                    </div>
                  );
                })}
              </div>
 
              {/* Continue Button Section */}
              <div className="flex flex-col items-center gap-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  disabled={!selectedProject}
                  onClick={handleContinue}
                  className={`px-12 h-14 text-lg text-white font-semibold rounded-xl shadow-lg
                    bg-gradient-to-r from-purple-600 to-blue-600
                    transition-all duration-300
                    ${
                      selectedProject
                        ? "hover:from-purple-700 hover:to-blue-700 hover:shadow-xl hover:scale-105 cursor-pointer"
                        : "opacity-50 cursor-not-allowed"
                    }`}
                >
                  Continue to Dashboard
                </button>

                {!selectedProject && (
                  <p className="text-sm text-gray-400 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-gray-400 animate-pulse"></span>
                    Select a project above to continue
                  </p>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>
      </div>
  );
};
 
export default SelectProject;